import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  patientDetailsAsync,
  resetPatientDetailsStatus,
  forceCheckInPatientAsync,
  resetForceCheckInPatientStatus,
  VisitRecord,
} from '../../../redux/flows/portal/lobby'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { APIStatus } from '../../../redux/types'

export const usePatientDetail = () => {
  const dispatch = useAppDispatch()
  const { visitId } = useParams<{ visitId: string }>()
  const patientDetails = useAppSelector((state) => state.lobbyPortal.patientDetails)
  const patientDetailsStatus = useAppSelector((state) => state.lobbyPortal.patientDetailsStatus)
  const patientDetailsError = useAppSelector((state) => state.lobbyPortal.patientDetailsError)
  const forceCheckInPatientStatus = useAppSelector((state) => state.lobbyPortal.forceCheckInPatientStatus)
  const queue = useAppSelector((state) => state.lobbyPortal.queue)
  const navigate = useNavigate()

  const [retryAttemptCount, setRetryAttemptCount] = useState(0)

  // Fetch patient details on mount or when visitId changes
  useEffect(() => {
    if (!visitId) return
    dispatch(patientDetailsAsync(visitId))
  }, [visitId, dispatch])

  // Handle retry
  const handleRetry = () => {
    if (visitId) {
      setRetryAttemptCount((prev) => prev + 1)
      dispatch(patientDetailsAsync(visitId))
    }
  }

  // Handle success - close popup and reset status
  useEffect(() => {
    if (patientDetailsStatus === APIStatus.fulfilled && retryAttemptCount > 0) {
      dispatch(resetPatientDetailsStatus())
      setRetryAttemptCount(0)
    }
  }, [patientDetailsStatus, retryAttemptCount, dispatch])

  // Handle retry failure - show updated message, clear status, close popup, navigate to lobby
  useEffect(() => {
    if (retryAttemptCount >= 1 && patientDetailsStatus === APIStatus.rejected) {
      const timer = setTimeout(() => {
        dispatch(resetPatientDetailsStatus())
        setRetryAttemptCount(0)
        navigate('/lobby')
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [retryAttemptCount, patientDetailsStatus, dispatch, navigate])

  // // Force check in patient
  // const handleForceCheckInPatient = () => {
  //   if (forceCheckInPatientStatus === APIStatus.pending || forceCheckInPatientStatus === APIStatus.fulfilled) return
  //   const currentVisit = patientDetails?.currentVisit
  //   if (currentVisit?.id && currentVisit?.clinicId) {
  //     dispatch(forceCheckInPatientAsync({ visitId: currentVisit.id, clinicId: currentVisit.clinicId }))
  //   }
  // }

  // Clean up
  useEffect(() => {
    return () => {
      dispatch(resetForceCheckInPatientStatus())
    }
  }, [dispatch])

  // Navigation between upcoming patients
  const { previousPatient, nextPatient, canGoPrevious, canGoNext } = useMemo(() => {
    if (!queue?.upcoming) {
      return { previousPatient: null, nextPatient: null, canGoPrevious: false, canGoNext: false }
    }

    const upcoming: VisitRecord[] = queue.upcoming
    const currentIndex = upcoming.findIndex((person) => person.visit?.id === patientDetails?.currentVisit?.id)
    if (currentIndex === -1) {
      return { previousPatient: null, nextPatient: null, canGoPrevious: false, canGoNext: false }
    }

    const prev = currentIndex > 0 ? upcoming[currentIndex - 1] : null
    const next = currentIndex < upcoming.length - 1 ? upcoming[currentIndex + 1] : null

    return {
      previousPatient: prev,
      nextPatient: next,
      canGoPrevious: currentIndex > 0,
      canGoNext: currentIndex < upcoming.length - 1,
    }
  }, [queue?.upcoming, patientDetails?.currentVisit?.id])

  const handlePreviousPatient = () => {
    if (previousPatient?.visit?.id) {
      navigate(`/patient-detail/${previousPatient.visit.id}`)
    }
  }

  const handleNextPatient = () => {
    if (nextPatient?.visit?.id) {
      navigate(`/patient-detail/${nextPatient.visit.id}`)
    }
  }

  return {
    currentPatient: patientDetails,
    currentVisit: patientDetails?.currentVisit,
    patientDetailsStatus,
    patientDetailsError,
    forceCheckInPatientStatus,
    handleRetry,
    // handleForceCheckInPatient,
    handlePreviousPatient,
    handleNextPatient,
    canGoPrevious,
    canGoNext,
    navigate,
  }
}
